import React, { useState, useEffect, useRef, useCallback } from 'react';
import handCursor from '../../assets/images/hand_cursor.png';

/**
 * Payment Logo Animation - Scroll Driven
 *
 * EXACT BEHAVIOR:
 * 1. Hand cursor picks up the ENTIRE payment icon box (dark green when picked)
 * 2. The box moves with the pinching hand cursor
 * 3. Box merges into the video and video starts playing
 * 4. Hand cursor disappears after placing
 */
const PaymentLogoAnimation = ({ videoRef }) => {
    const [scrollProgress, setScrollProgress] = useState(0);
    const containerRef = useRef(null);
    const [paymentsCardRect, setPaymentsCardRect] = useState(null);
    const [videoRect, setVideoRect] = useState(null);

    // Get element positions
    const updatePositions = useCallback(() => {
        const paymentsCard = document.getElementById('payments-card');
        const videoContainer = document.getElementById('success-video-container');

        if (paymentsCard) {
            setPaymentsCardRect(paymentsCard.getBoundingClientRect());
        }
        if (videoContainer) {
            setVideoRect(videoContainer.getBoundingClientRect());
        }
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current) return;

            updatePositions();

            const rect = containerRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            const startOffset = windowHeight * 0.7;
            const endOffset = -rect.height + windowHeight * 0.3;

            if (rect.top <= startOffset && rect.top >= endOffset) {
                const totalDistance = startOffset - endOffset;
                const scrolled = startOffset - rect.top;
                const progress = Math.max(0, Math.min(1, scrolled / totalDistance));
                setScrollProgress(progress);
            } else if (rect.top > startOffset) {
                setScrollProgress(0);
            } else {
                setScrollProgress(1);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', updatePositions);
        handleScroll();
        updatePositions();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', updatePositions);
        };
    }, [updatePositions]);

    // Video control
    useEffect(() => {
        if (videoRef?.current) {
            // Play only if not fully visible margin? No, 0.9 threshold.
            if (scrollProgress >= 0.9) {
                // Only play if not already playing AND not ended
                // This prevents restarting if the user keeps scrolling after video finishes
                if (videoRef.current.paused && !videoRef.current.ended) {
                    videoRef.current.play().catch(() => { });
                }
            } else {
                // If we scroll back up, pause and reset
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        }
    }, [scrollProgress, videoRef]);

    // Hide entire payments card when animation active (we show animated version)
    useEffect(() => {
        const paymentsCard = document.getElementById('payments-card');
        if (paymentsCard) {
            paymentsCard.style.opacity = scrollProgress > 0.05 ? '0' : '1';
        }
    }, [scrollProgress]);

    // Calculate positions
    const getStartPosition = () => {
        if (!paymentsCardRect) return { x: 100, y: 200 };
        // Start at the exact position of the payments card
        return {
            x: paymentsCardRect.left,
            y: paymentsCardRect.top,
        };
    };

    const getEndPosition = () => {
        if (!videoRect) return { x: 200, y: 400 };
        // End at center of video
        return {
            x: videoRect.left + videoRect.width / 2 - 40, // 40 = half of 80px box
            y: videoRect.top + 40,
        };
    };

    // Box position - centered with cursor
    const getBoxPosition = () => {
        const start = getStartPosition();
        const end = getEndPosition();
        const p = scrollProgress;

        return {
            x: start.x + (end.x - start.x) * p,
            y: start.y + (end.y - start.y) * p,
        };
    };

    // Cursor position - pinching hand holds the box
    const getCursorPosition = () => {
        const boxPos = getBoxPosition();
        // Position hand so fingers appear to grip the box from the right side
        return {
            x: boxPos.x + 20,
            y: boxPos.y + 50,
        };
    };

    // Scale down the box as it approaches the video (merge effect)
    const getBoxScale = () => {
        if (scrollProgress < 0.7) return 1;
        // Scale from 1 to 0.3 between 0.7 and 0.9
        const scaleProgress = (scrollProgress - 0.7) / 0.2;
        return 1 - (scaleProgress * 0.7);
    };

    // Opacity for merge effect
    const getBoxOpacity = () => {
        if (scrollProgress < 0.8) return 1;
        // Fade from 1 to 0 between 0.8 and 0.9
        return 1 - ((scrollProgress - 0.8) / 0.1);
    };

    // Cursor visible only during movement
    const isCursorVisible = scrollProgress > 0.02 && scrollProgress < 0.92;

    // Box visible only during movement - disappears as it merges
    const isBoxVisible = scrollProgress > 0.02 && scrollProgress < 0.9;

    // Don't render anything if not scrolled
    if (scrollProgress === 0) {
        return <div ref={containerRef} className="h-[100px]" />;
    }

    return (
        <div ref={containerRef} className="h-[100px] relative">
            {/* ENTIRE PAYMENT ICON BOX - picked up by hand */}
            {isBoxVisible && (
                <div
                    style={{
                        position: 'fixed',
                        left: getBoxPosition().x,
                        top: getBoxPosition().y,
                        width: '80px',
                        height: '80px',
                        backgroundColor: 'var(--pluckr-green)',
                        borderRadius: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99998, // Behind hand cursor
                        pointerEvents: 'none',
                        transform: `scale(${getBoxScale()})`,
                        opacity: getBoxOpacity(),
                        transition: 'transform 0.1s ease-out, opacity 0.1s ease-out',
                    }}
                >
                    <img
                        src="/payment-logos/razorpay.png"
                        alt="Razorpay"
                        style={{
                            width: '36px',
                            height: '36px',
                            objectFit: 'contain',
                        }}
                    />
                    <span style={{
                        fontSize: '8px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#1a1a1a',
                        marginTop: '4px',
                    }}>
                        PAYMENTS
                    </span>
                </div>
            )}

            {/* HAND CURSOR - in front of icon */}
            {isCursorVisible && (
                <img
                    src={handCursor}
                    alt=""
                    style={{
                        position: 'fixed',
                        left: getCursorPosition().x,
                        top: getCursorPosition().y,
                        width: '50px',
                        height: '50px',
                        zIndex: 99999, // In front of icon
                        pointerEvents: 'none',
                    }}
                />
            )}
        </div>
    );
};

export default PaymentLogoAnimation;
